import { Component, ElementRef, Input, Optional, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { SwodocNodeText } from 'src/app/shared/components/document/document-edit/nodes/text/swodoc-node-text';

import { IBaseInputContract } from '../contracts/base-input';

/**
 * Text input that only accepts a non-negative German decimal number: digits
 * and a single comma, with at most {@link maxDecimals} decimal digits. Every
 * other character is removed while the user types.
 *
 * Mimics ui behaviour of {@link SwodocNodeText} with `globalType` 148, 149, 150 or 181 so the user sees the same behavior, but more elegant and reusable.
 */
@Component({
  selector: 'itl-decimal',
  templateUrl: './decimal.component.html',
  styleUrls: ['../_templates/text-input-template/text-input-template-alb-class.scss'],
})
export class DecimalComponent implements IBaseInputContract, ControlValueAccessor {
  @Input() public title: string;
  @Input() public placeholder = '';
  @Input() public icon: string;
  /** Maximum number of decimal digits the user can type: `1` → `9,1`, `2` → `9,12`; `0` removes the comma entirely. */
  @Input() public maxDecimals = 1;
  // zeigt einen Counter an wie viele Zeichen noch möglich sind und sperrt weitere eingaben
  @Input() public maxlength: number;
  // Zeigt einen roten Stern neben dem Titel an
  @Input() public required = false;
  // Es wird kein Icon angezeigt
  @Input() public noIcon = false;
  // Graut die Komponente aus und keine Interaktion mehr möglich - stetzt auch die readonly property auf den Text Input
  @Input() public disabled = false;
  @Input() public description: string;

  @ViewChild('input', { static: true }) public input: ElementRef;

  public onChange = (text: string) => {};
  public onTouched = () => {};

  constructor(@Self() @Optional() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public setFocus() {
    this.input.nativeElement.focus();
  }

  public markAsTouched(): void {
    this.onTouched();
  }

  public writeValue(value: string | null | undefined) {
    this.input.nativeElement.value = value ?? '';
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public onInput(event: Event): void {
    const element = event.target as HTMLInputElement;
    const rawValue = element.value;
    const sanitized = this.sanitize(rawValue);
    if (sanitized !== rawValue) {
      const caret = Math.max(0, (element.selectionStart ?? sanitized.length) - (rawValue.length - sanitized.length));
      element.value = sanitized;
      element.setSelectionRange(caret, caret);
    }
    this.onChange(sanitized);
  }

  /**
   * Removes everything but digits, keeps only the leftmost comma and cuts the
   * decimal part after {@link maxDecimals} digits. A trailing comma is kept so
   * the user can continue typing the decimals.
   */
  private sanitize(value: string): string {
    const result = (value ?? '').replace(/[^0-9,]/g, '');
    if (this.maxDecimals <= 0) {
      return result.replace(/,/g, '');
    }
    const firstCommaIndex = result.indexOf(',');
    if (firstCommaIndex === -1) {
      return result;
    }
    const decimals = result
      .slice(firstCommaIndex + 1)
      .replace(/,/g, '')
      .slice(0, this.maxDecimals);
    return result.slice(0, firstCommaIndex + 1) + decimals;
  }
}
